import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import * as API from "../../Endpoints/Endpoints";

const socket = io(API.SOCKET_URL, {
    transports: ["websocket", "polling"],
});

const OnlinePlayers = () => {
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [email, setEmail] = useState("");
    const [userId, setUserId] = useState(null);
    const [invitedUsers, setInvitedUsers] = useState([]);
    const [acceptedUsers, setAcceptedUsers] = useState([]);
    const navigate = useNavigate();

    // Fetch user email from JWT token
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                const userId = parseInt(decoded.id, 10);
                setUserId(userId);
                setEmail(decoded.email);
                localStorage.setItem("id", userId);
                console.log("📢 Sending email to backend:", decoded.email);
                socket.emit("userConnected", decoded.email);
            } catch (error) {
                console.error("❌ Invalid token:", error);
                localStorage.removeItem("token");
            }
        }
    }, []);

    // Fetch online users
    useEffect(() => {
        socket.on("onlineUsers", (users) => {
            setOnlineUsers(users.filter(user => user.email !== email));
        });

        socket.on("userDisconnected", (disconnectedUser) => {
            setOnlineUsers(prevUsers => prevUsers.filter(user => user.email !== disconnectedUser));
        });

        return () => {
            socket.off("onlineUsers");
            socket.off("userDisconnected");
        };
    }, [email]);

    // Send game request
    const sendGameRequest = (to) => {
        if (invitedUsers.length >= 3) {
            alert("You can invite up to 4 players only.");
            return;
        }

        socket.emit("sendGameRequest", { from: email, to });
        setInvitedUsers((prev) => [...prev, to]);
        console.log(`📤 Sent game request from ${email} to ${to}`);
    };

    // Listen for incoming game requests
    useEffect(() => {
        socket.on("gameRequest", ({ from }) => {
            console.log(`🎮 Received game request from ${from}`);

            if (window.confirm(`${from} invited you to play. Accept?`)) {
                socket.emit("respondGameRequest", { from, to: email, accepted: true });
                console.log(`✔️ Sent game request response from ${email} to ${from}`);

                // Navigate to game directly upon acceptance
                navigate("/addplaydb");
            }
        });

        return () => {
            socket.off("gameRequest");
        };
    }, [email, navigate]);

    // Listen for game request acceptance
    useEffect(() => {
        socket.on("gameRequestAccepted", ({ from, to }) => {
            console.log(`✅ Game request accepted: ${from} -> ${to}`);

            if (to === email || from === email) {
                setAcceptedUsers((prev) => [...prev, from, to]);
            }
        });

        return () => {
            socket.off("gameRequestAccepted");
        };
    }, [email]);

    // Handle game request response
    useEffect(() => {
        socket.on("gameRequestResponse", ({ from, to, accepted }) => {
            console.log(`📩 Received game request response from ${from}: ${accepted ? "Accepted" : "Rejected"}`);

            if (accepted && to === email) {
                console.log(`🔄 Updating acceptedUsers: ${from}`);
                setAcceptedUsers((prev) => [...prev, from]);
            }
        });

        return () => {
            socket.off("gameRequestResponse");
        };
    }, [email]);

    // Start game for all accepted users
    const startGame = () => {
        if (acceptedUsers.length === 0) {
            alert("At least one player must accept the request to start the game.");
            return;
        }
    
        console.log(`🚀 Starting game with: ${acceptedUsers}`);
        socket.emit("startGame", { host: email, players: acceptedUsers });
    
        // ✅ Navigate all connected players to addplaydb and pass player list
        navigate("/addplaydb", { state: { players: acceptedUsers } });
    };    

    return (
        <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Online Players</h2>
            <ul>
                {onlineUsers.length === 0 ? (
                    <p>No players online</p>
                ) : (
                    onlineUsers.map((user, index) => (
                        <li key={index} className="flex justify-between items-center p-2 border-b">
                            <span>{user.email}</span>

                            {acceptedUsers.includes(user.email) ? (
                                <span className="text-green-500">Accepted ✅</span>
                            ) : invitedUsers.includes(user.email) ? (
                                <span className="text-red">Invited</span>
                            ) : (
                                <button onClick={() => sendGameRequest(user.email)}>Invite</button>
                            )}
                        </li>
                    ))
                )}
            </ul>

            {/* Start Game Button (Visible only if at least one user has accepted) */}
            {acceptedUsers.length > 0 && (
                <div className="mt-4">
                <button
                    className="mt-4 bg-[#e64a32] text-white px-4 py-2 rounded"
                    onClick={startGame}
                >
                    Start Game
                </button> </div>
            )}

            <button className="mt-4 bg-gray-500 text-white px-4 py-2 rounded" onClick={() => navigate("/playdbtable")}>
                Back
            </button>
        </div>
    );
};

export default OnlinePlayers;
